/**
 * Talk-o 应用首页
 *
 * 功能汇总主页：将聊天伙伴、同声传译等功能以卡片形式呈现，
 * 方便后续扩展更多功能模块。
 */

import { useState, useEffect } from 'react'
import ParentDashboard from './pages/ParentDashboard'
import AdminDashboard from './pages/AdminDashboard'
import TalkoChat from './pages/TalkoChat'
import LiveTranslate from './pages/LiveTranslate'
import LiveTranslateBidirectional from './pages/LiveTranslateBidirectional'
import Login from './pages/Login'
import Register from './pages/Register'
import Verify from './pages/Verify'
import MemberDashboard from './pages/MemberDashboard'

// =============================================================================
// 功能卡片配置
// =============================================================================

interface FeatureCardProps {
  href: string
  emoji: string
  title: string
  description: string
  gradient: string
  shadow: string
}

function FeatureCard({ href, emoji, title, description, gradient, shadow }: FeatureCardProps) {
  return (
    <a
      href={href}
      className={`
        group relative overflow-hidden
        rounded-[2rem] p-6 sm:p-8
        bg-white/70 backdrop-blur-xl
        border border-white/60
        shadow-[0_12px_40px_-12px_rgba(100,116,139,0.18)]
        transition-all duration-300 ease-out
        hover:scale-[1.02] hover:shadow-[0_20px_60px_-15px_rgba(100,116,139,0.25)]
        flex flex-col items-start gap-4
      `}
    >
      {/* 渐变装饰 */}
      <div
        className={`
          absolute -right-8 -top-8 w-32 h-32 rounded-full
          bg-gradient-to-br ${gradient} opacity-20 blur-2xl
          group-hover:opacity-30 transition-opacity
        `}
      />

      <div
        className={`
          relative z-10 w-14 h-14 rounded-2xl
          bg-gradient-to-br ${gradient}
          text-white text-2xl
          flex items-center justify-center
          shadow-lg ${shadow}
        `}
      >
        {emoji}
      </div>

      <div className="relative z-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-1">{title}</h2>
        <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
      </div>

      <div className="relative z-10 mt-auto pt-2 flex items-center text-sm font-semibold text-slate-600 group-hover:text-slate-800 transition-colors">
        <span>开始使用</span>
        <svg
          className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </a>
  )
}

function HomeHub() {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 flex flex-col items-center justify-center p-4 safe-area-inset-top safe-area-inset-bottom">
      {/* 背景装饰 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 w-96 h-96 rounded-full bg-slate-200/40 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-3xl">
        {/* 标题区 */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-sky-500 to-indigo-500 shadow-lg shadow-sky-300/40 mb-5">
            <span className="text-4xl" role="img" aria-label="Talk-o">
              🎙️🌐💬
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-sky-600 via-indigo-500 to-slate-700 bg-clip-text text-transparent tracking-tight mb-3">
            Talk-o
          </h1>
          <p className="text-slate-500 text-base sm:text-lg font-medium">
            AI 实时翻译 · 语音对话 · 跨语言沟通
          </p>
        </div>

        {/* 功能卡片网格 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <FeatureCard
            href="/chat"
            emoji="💬"
            title="聊天伙伴"
            description="按住说话、松开发送，把 AI 当作语言练习伙伴进行对话。"
            gradient="from-sky-500 to-indigo-500"
            shadow="shadow-sky-300/50"
          />

          <FeatureCard
            href="/translate"
            emoji="🌐"
            title="同声传译"
            description="麦克风常开，边说边译，实时将你的话语转换为外语语音。"
            gradient="from-cyan-500 to-sky-500"
            shadow="shadow-cyan-300/50"
          />

          <FeatureCard
            href="/translate-bidirectional"
            emoji="🔄"
            title="双向同传"
            description="双通道并发互译，自动检测语种并生成长文归档。"
            gradient="from-indigo-500 to-cyan-500"
            shadow="shadow-indigo-300/50"
          />
        </div>

        {/* 底部辅助链接 */}
        <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-400 font-medium">
          <a href="/member" className="hover:text-slate-600 transition-colors flex items-center gap-1">
            <span>👤</span>
            <span>会员中心</span>
          </a>
          <span>·</span>
          <a href="/admin" className="hover:text-slate-600 transition-colors flex items-center gap-1">
            <span>👑</span>
            <span>管理员后台</span>
          </a>
          <span>·</span>
          <a href="/parent" className="hover:text-slate-600 transition-colors flex items-center gap-1">
            <span>⚙️</span>
            <span>角色与对话管理</span>
          </a>
          <span>·</span>
          <span>Talk-o v1.0</span>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// 简易路由（无需引入 react-router）
// =============================================================================

function App() {
  const [pathname, setPathname] = useState(() => window.location.pathname)

  useEffect(() => {
    // 支持浏览器前后退进时刷新路径状态
    const handlePopState = () => setPathname(window.location.pathname)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  switch (pathname) {
    case '/parent':
      return <ParentDashboard />
    case '/admin':
      return <AdminDashboard />
    case '/chat':
      return <TalkoChat />
    case '/translate':
      return <LiveTranslate />
    case '/translate-bidirectional':
      return <LiveTranslateBidirectional />
    case '/login':
      return <Login />
    case '/register':
      return <Register />
    case '/verify':
      return <Verify />
    case '/member':
      return <MemberDashboard />
    default:
      return <HomeHub />
  }
}

export default App
